/**
 * PLAT-10740: pure helpers for persisting the Transcript & Speakers view state
 * (selected language + audio/dub mode) in the URL query string, so a refresh —
 * and a shared link — restores the same view instead of resetting to Original.
 *
 * PLAT-10948 (follow-up): the Translation Review workspace opened from this
 * surface is shareable too, through a single `translationReview=<granularity>`
 * param — its PRESENCE restores the workspace open, its VALUE restores the
 * view-mode granularity (Paragraph / Segment / Fragment). One param because the
 * granularity is meaningless with the workspace closed; a value that isn't a
 * granularity still opens, on the Paragraph default. Review always reviews the
 * SELECTED language, so it is written only alongside a `lang`, dropped otherwise.
 *
 * PLAT-10926 extends that to the workspace's two remaining view attributes, so a
 * restored link lands on the same tab and reference column and not just the same
 * language and granularity: `reviewPanel` (the right-hand tab) and `reviewRef`
 * (the left column's proxy reference language). Both obey exactly the
 * `translationReview` rule — written only while the review is open on a selected
 * language, dropped wholesale with it. They are prefixed rather than named
 * `panel` / `ref` deliberately: this tab stays mounted for the whole Content
 * Studio session and DELETES every param it owns on close, so a generic name
 * here would silently eat another surface's param — the exact bug PLAT-10948
 * fixed when the clip review wrote `translationView`.
 *
 * The React wiring lives in `TranscriptSpeakersContent`; the validation +
 * param-shaping decisions live here so they can be unit-tested without
 * rendering the full store-connected component.
 */

import {
  parseTranslationViewMode,
  type TranslationViewMode,
} from '@src/components/transcript/alignedBlocks';

import type {
  ReviewMode,
  ReviewRightTab,
} from '@src/components/transcript/TranslationReviewWorkspace';

export type ViewAudioMode = 'original' | 'dubbed' | 'both';

const LANG_PARAM = 'lang';
const VOICE_PARAM = 'voice';
const REVIEW_PARAM = 'translationReview';
const PANEL_PARAM = 'reviewPanel';
const REF_PARAM = 'reviewRef';
/** PLAT-11662 — which job the review is on: `translation` | `dubbing`. */
const MODE_PARAM = 'reviewMode';

const isReviewMode = (value: string | null): value is ReviewMode =>
  value === 'translation' || value === 'dubbing';

const PANELS: readonly ReviewRightTab[] = [
  'changes',
  'translationMemory',
  'summary',
  'speakers',
];

const isPanel = (value: string | null): value is ReviewRightTab =>
  PANELS.some(panel => panel === value);

/**
 * Every param this surface owns. `buildViewParams` rebuilds the whole query
 * string and this tab stays mounted for the entire Content Studio session
 * (`ContentView` only toggles visibility), so any key another surface writes
 * must stay out of this set — see the clip preview's `CLIP_PREVIEW_PARAMS`
 * A disjointness test guards the pair.
 */
export const TRANSCRIPT_VIEW_PARAMS = [
  LANG_PARAM,
  VOICE_PARAM,
  REVIEW_PARAM,
  PANEL_PARAM,
  REF_PARAM,
  MODE_PARAM,
] as const;

/**
 * Whether the URL is asking for the Translation Review workspace on this surface.
 * The one place that knows both the param name and its contract (presence, since
 * the value carries the granularity) — so a consumer that only needs "is a review
 * being restored?" can't drift from what hydration actually does. `resolveHydrated
 * ViewState` additionally requires a valid language before it opens.
 */
export function isTranscriptReviewRequested(params: URLSearchParams): boolean {
  return params.get(REVIEW_PARAM) !== null;
}

/**
 * PLAT-12055 — whether the open review's params have actually landed in the
 * URL. This, not "we called navigate", is what makes the URL authoritative
 * for `shouldCloseReviewFromUrl`: the router delivers a new query string a
 * render later, and any state change in that gap re-runs the write-back
 * with the OLD params, which carry no review param. Counting the review as
 * written at that moment closed it an instant after Review Dub opened it.
 */
export function isReviewWrittenToUrl({
  reviewOpen,
  selectedLanguage,
  params,
}: {
  reviewOpen: boolean;
  selectedLanguage: string | null;
  params: URLSearchParams;
}): boolean {
  return (
    reviewOpen && !!selectedLanguage && isTranscriptReviewRequested(params)
  );
}

/**
 * PLAT-11656 — whether an open review should close because the URL lost its
 * review param from OUTSIDE this surface (the app sidebar is clickable beside
 * the page-layout review, and its links rebuild the query string).
 *
 * `reviewSynced` is whether this surface has already written the open review
 * into the URL: before it has, an absent param just means "not written yet"
 * (the review was opened by a click and the write-back is about to add it);
 * after it has, an absent param can only mean someone else removed it.
 */
export function shouldCloseReviewFromUrl({
  reviewOpen,
  reviewSynced,
  params,
}: {
  reviewOpen: boolean;
  reviewSynced: boolean;
  params: URLSearchParams;
}): boolean {
  return reviewOpen && reviewSynced && !isTranscriptReviewRequested(params);
}

export interface HydrateInput {
  /** Raw `lang` query param (BCP-47), or null when absent. */
  langParam: string | null;
  /** Raw `voice` query param, or null when absent. */
  voiceParam: string | null;
  /**
   * Raw `translationReview` query param: present (any value) ⇒ the review was
   * open, and the value carries its view-mode granularity.
   */
  reviewParam: string | null;
  /**
   * Raw `reviewPanel` query param (the right-hand tab). Optional: a caller that
   * only cares about language/voice/granularity keeps its existing call shape,
   * and an absent value resolves to the workspace's own default.
   */
  panelParam?: string | null;
  /**
   * Raw `reviewRef` query param — the left column's proxy reference language.
   * Optional for the same reason as `panelParam`.
   */
  refParam?: string | null;
  /** Raw `reviewMode` query param (PLAT-11662). Optional, like `panelParam`. */
  modeParam?: string | null;
  /** BCP-47 codes the org supports (validates an unknown `lang` back to null). */
  supportedCodes: readonly string[];
  /** BCP-47 codes that already have a dub. */
  dubbedLanguages: readonly string[];
  /** Whether the dubbing entry point is enabled (flag + activation gate). */
  dubEntryEnabled: boolean;
  /**
   * Whether the review entry point is enabled (`enableTranslationReview`).
   * PLAT-10926: a stale or hand-edited link must not open a workspace the flag
   * has turned off. Optional and defaulting to true so every existing caller
   * keeps its current behaviour; the Transcript & Speakers host passes the real
   * flag.
   */
  reviewEnabled?: boolean;
}

export interface HydratedViewState {
  language: string | null;
  audioMode: ViewAudioMode;
  /** Whether to reopen the Translation Review workspace on load. */
  reviewOpen: boolean;
  /** The review's initial view-mode (defaults to Paragraph). */
  reviewViewMode: TranslationViewMode;
  /** The review's initial right-hand tab (defaults to Changes). */
  reviewPanel: ReviewRightTab;
  /** The review's initial proxy reference language (null ⇒ the true original). */
  reviewReferenceLanguage: string | null;
  /**
   * The review's initial job (PLAT-11662); null when the link carries none, so
   * the workspace falls back to its own default (Dubbing if the language has a
   * dub).
   */
  reviewMode: ReviewMode | null;
}

/**
 * Resolve the initial view state from the URL params, validated against the
 * languages the story actually supports/has-dubbed.
 *
 * - Unknown `lang` (not org-supported) ⇒ Original.
 * - `voice` only applies to a language that has a dub: `original` / `dubbed` /
 *   `both` are honored, anything else defaults to `dubbed` (matching the
 *   language-switch auto-select). A language without a dub ⇒ `original`
 *   (Dubbed would mute the video with silence).
 * - `translationReview` only reopens the review when there is a valid selected
 *   language to review; its value restores the granularity (else the Paragraph
 *   default, which also covers a present-but-unparseable value).
 * - `reviewPanel` restores the right-hand tab, and `reviewRef` the left column's
 *   proxy reference language. Unknown values fall back to the workspace defaults,
 *   and a `reviewRef` equal to the reviewed language (or not org-supported) back
 *   to the true original. Both are ignored unless the review actually reopens, so
 *   an orphaned attribute can't leak into a closed workspace.
 */
export function resolveHydratedViewState({
  langParam,
  voiceParam,
  reviewParam,
  panelParam = null,
  refParam,
  modeParam = null,
  supportedCodes,
  dubbedLanguages,
  dubEntryEnabled,
  reviewEnabled = true,
}: HydrateInput): HydratedViewState {
  const language =
    langParam && supportedCodes.includes(langParam) ? langParam : null;

  const hasDub =
    dubEntryEnabled && !!language && dubbedLanguages.includes(language);

  const audioMode: ViewAudioMode = hasDub
    ? voiceParam === 'original'
      ? 'original'
      : voiceParam === 'both'
        ? 'both'
        : 'dubbed'
    : 'original';

  // Review is language-scoped: without a valid selected language there is
  // nothing to review, so an orphaned `translationReview` stays closed. Presence
  // alone opens it — the value only picks the granularity, so an unparseable one
  // still opens on the default view rather than swallowing the link's intent.
  // The flag
  // gate (PLAT-10926) means a stale or forged link falls back to the plain
  // transcript rather than opening a workspace that is turned off.
  const reviewOpen = reviewEnabled && reviewParam !== null && !!language;
  const reviewViewMode = parseTranslationViewMode(reviewParam) ?? 'segment';

  return {
    language,
    audioMode,
    reviewOpen,
    reviewViewMode,
    reviewPanel: reviewOpen && isPanel(panelParam) ? panelParam : 'changes',
    reviewReferenceLanguage:
      reviewOpen &&
      refParam &&
      refParam !== language &&
      supportedCodes.includes(refParam)
        ? refParam
        : null,
    reviewMode: reviewOpen && isReviewMode(modeParam) ? modeParam : null,
  };
}

export interface WriteInput {
  selectedLanguage: string | null;
  audioMode: ViewAudioMode;
  dubEntryEnabled: boolean;
  dubbedLanguages: readonly string[];
  /** Whether the Translation Review workspace is currently open. */
  reviewOpen: boolean;
  /** The review's current view-mode granularity. */
  reviewViewMode: TranslationViewMode;
  /**
   * The review's current right-hand tab. Optional so a caller that doesn't track
   * it keeps its existing call shape; absent writes the workspace's default.
   */
  reviewPanel?: ReviewRightTab;
  /**
   * The review's current proxy reference language (null ⇒ the true original).
   * Optional for the same reason as `reviewPanel`.
   */
  reviewReferenceLanguage?: string | null;
  /**
   * The review's current job (PLAT-11662). Optional for the same reason; absent
   * writes nothing, so a link without a mode keeps today's meaning.
   */
  reviewMode?: ReviewMode | null;
}

/**
 * Write the current view state into a copy of `base`, preserving any unrelated
 * params. `lang` is set for a selected language (dropped for Original); `voice`
 * is set — including `original` — only for a language that has a dub, so a
 * user's explicit Original on a dubbed language round-trips while other cases
 * stay clean. `translationReview` carries the granularity as its value and is
 * written only while the review is open on a selected language, dropped otherwise
 * (so closing the review — or clearing the language — leaves a clean URL).
 * `reviewPanel` rides along on the same condition (always written while open, so
 * the link is explicit); `reviewRef` only when a proxy reference language is
 * actually picked. Returns a new `URLSearchParams`.
 */
export function buildViewParams(
  base: URLSearchParams,
  {
    selectedLanguage,
    audioMode,
    dubEntryEnabled,
    dubbedLanguages,
    reviewOpen,
    reviewViewMode,
    reviewPanel = 'changes',
    reviewReferenceLanguage = null,
    reviewMode = null,
  }: WriteInput,
): URLSearchParams {
  const params = new URLSearchParams(base);

  if (selectedLanguage) params.set(LANG_PARAM, selectedLanguage);
  else params.delete(LANG_PARAM);

  if (
    selectedLanguage &&
    dubEntryEnabled &&
    dubbedLanguages.includes(selectedLanguage)
  ) {
    params.set(VOICE_PARAM, audioMode);
  } else {
    params.delete(VOICE_PARAM);
  }

  if (selectedLanguage && reviewOpen) {
    params.set(REVIEW_PARAM, reviewViewMode);
    params.set(PANEL_PARAM, reviewPanel);
    if (reviewReferenceLanguage) {
      params.set(REF_PARAM, reviewReferenceLanguage);
    } else {
      params.delete(REF_PARAM);
    }
    if (reviewMode) {
      params.set(MODE_PARAM, reviewMode);
    } else {
      params.delete(MODE_PARAM);
    }
  } else {
    params.delete(REVIEW_PARAM);
    params.delete(PANEL_PARAM);
    params.delete(REF_PARAM);
    params.delete(MODE_PARAM);
  }

  return params;
}
